import type { SettingsView } from "@widgets/settings-view-tabs/settings-view-tabs";

/** Sections inside the Partner settings view. */
export type PartnerSection = "partner" | "models" | "ocr";

export const PARTNER_SECTIONS: PartnerSection[] = ["partner", "models", "ocr"];

export const isPartnerSection = (value: unknown): value is PartnerSection =>
	PARTNER_SECTIONS.includes(value as PartnerSection);

/**
 * `view` defaults to "model" (see the route's validateSearch), which used to be
 * safe because the route guard only ever admitted model_settings / scoring_settings
 * / root users. `ocr_settings`-only users can reach the page too, so an explicit
 * "model" request (or the unrequested default) only wins when the user can
 * actually open the model plane — otherwise they fall through to the partner
 * plane, where their only visible section is OCR.
 */
export const resolveSettingsView = (
	requestedView: SettingsView,
	{
		canOpenModelView,
		canOpenPartnerView,
	}: { canOpenModelView: boolean; canOpenPartnerView: boolean },
): SettingsView => {
	if (requestedView === "partner" && canOpenPartnerView) return "partner";
	if (canOpenModelView) return "model";
	return "partner";
};

/** The Partner section (editing the partner's own profile) is root-only.
 * Models and OCR each also open for their matching settings permission alone
 * — `model_settings` for Models, `ocr_settings` for OCR — so a role can see
 * and drill into the roster it's scoped to without full partner
 * administration. Root always sees every section. */
export const resolveVisiblePartnerSections = (
	canAdministerPartner: boolean,
	hasModelSettings: boolean,
	hasOcrSettings: boolean,
): PartnerSection[] =>
	PARTNER_SECTIONS.filter((section) => {
		if (section === "partner") return canAdministerPartner;
		if (section === "models") return canAdministerPartner || hasModelSettings;
		return canAdministerPartner || hasOcrSettings;
	});

export type PartnerFormState = {
	name: string;
	authorization_scheme: string;
	default_language: string;
	default_currency: string;
	country: string;
	is_active: boolean;
	has_ocr: boolean;
	ocr_required: boolean;
	ocr_in_pdf: boolean;
	has_shap: boolean;
	has_recommendations: boolean;
	has_gpt_expl: boolean;
	has_ifrs: boolean;
	has_evidently_monitoring: boolean;
	auto_generate_client_app_id: boolean;
	own_edit_deadline_hour: number | null;
	model_change_approval: boolean;
};

/** Models are split by `is_active`: active models live in the Active tab, deactivated ones are treated as archived. */
export type ModelsTab = "active" | "archived";

export const toInputString = (value?: string | null) => value ?? "";
